"use client";

import { reportError } from "@/actions/error-report";
import { Icon } from "@iconify/react";
import { Cinzel, Inter, Spectral } from "next/font/google";
import { useEffect, useState } from "react";
import "./globals.css";

const inter = Inter({ subsets: ["latin"], variable: "--font-inter" });
const cinzel = Cinzel({ subsets: ["latin"], variable: "--font-cinzel" });
const spectral = Spectral({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
  variable: "--font-spectral",
});

function isChunkError(message: string): boolean {
  return (
    message.includes("module factory is not available") ||
    message.includes("Loading chunk") ||
    message.includes("ChunkLoadError")
  );
}

const STRINGS = {
  fi: {
    title: "Jokin meni pieleen",
    description:
      "Odottamaton virhe. Yritä uudelleen tai palaa etusivulle.",
    errorId: "Virhetunniste",
    tryAgain: "Yritä uudelleen",
    goHome: "Etusivulle",
  },
  en: {
    title: "Something went wrong",
    description:
      "An unexpected error occurred. Please try again or return to the home page.",
    errorId: "Error ID",
    tryAgain: "Try again",
    goHome: "Go to home",
  },
} as const;

function detectLocale(): "fi" | "en" {
  if (typeof window === "undefined") return "fi";
  const seg = window.location.pathname.split("/")[1];
  return seg === "en" ? "en" : "fi";
}

/**
 * Global error boundary - replaces the root layout entirely, so it must
 * import globals.css and the font variables itself. Cannot use next-intl
 * here because NextIntlClientProvider is not mounted at this level.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [locale, setLocale] = useState<"fi" | "en">("fi");

  useEffect(() => {
    setLocale(detectLocale());
  }, []);

  useEffect(() => {
    console.error("Global error boundary caught:", error);

    const message = error.message || "";

    const isChunk = isChunkError(message);
    const reloadKey = "chunk-reload-" + window.location.pathname;
    const alreadyReloaded = sessionStorage.getItem(reloadKey);

    const describe = (e: unknown): string => {
      if (e == null) return `thrown value: ${String(e)}`;
      if (typeof e !== "object") return `thrown ${typeof e}: ${String(e)}`;
      const ctor = (e as { constructor?: { name?: string } }).constructor?.name;
      const name = (e as { name?: string }).name;
      const own = Object.getOwnPropertyNames(e);
      let serialized = "";
      try {
        serialized = JSON.stringify(
          e,
          Object.getOwnPropertyNames(e as object),
        );
      } catch {
        serialized = "(unserializable)";
      }
      return `${name || ctor || "Object"} (own: ${own.join(",")}) ${serialized}`;
    };

    let reportMessage = error.message || `Unknown error — ${describe(error)}`;
    if (isChunk) {
      reportMessage = alreadyReloaded
        ? `[Chunk Error - Reload Failed] ${reportMessage}`
        : `[Chunk Error - Reloading] ${reportMessage}`;
    }

    const reportKey =
      "error-reported-" +
      (isChunk ? (alreadyReloaded ? "post-" : "pre-") : "") +
      (error.digest || error.message);
    if (!sessionStorage.getItem(reportKey)) {
      sessionStorage.setItem(reportKey, "1");
      reportError({
        message: reportMessage,
        stack: error.stack,
        digest: error.digest,
        url: typeof window !== "undefined" ? window.location.href : "unknown",
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      }).catch(console.error);
    }

    if (isChunk && !alreadyReloaded) {
      sessionStorage.setItem(reloadKey, "1");
      const url = new URL(window.location.href);
      url.searchParams.set("_r", Date.now().toString());
      window.location.replace(url.toString());
      return;
    }
  }, [error]);

  const t = STRINGS[locale];

  return (
    <html
      lang={locale}
      className={`${inter.variable} ${cinzel.variable} ${spectral.variable} medieval`}
      data-theme="medieval"
      style={{ colorScheme: "light" }}
      suppressHydrationWarning
    >
      <body className="bg-background text-foreground">
        <main className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
          <div className="w-full max-w-md text-center">
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 -z-10 rounded-full bg-amber-500/10 blur-3xl" />
                <Icon
                  icon="game-icons:tower-fall"
                  className="text-primary"
                  width={120}
                  height={120}
                />
              </div>
            </div>

            <h1
              className="mb-3 font-bold text-3xl sm:text-4xl text-foreground"
              style={{ fontFamily: "var(--font-cinzel), serif" }}
            >
              {t.title}
            </h1>

            <p className="mb-8 text-default-500">{t.description}</p>

            {error.digest && (
              <p className="mb-6 font-mono text-xs text-default-400">
                {t.errorId}: {error.digest}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={() => reset()}
                className="rounded-medium bg-primary px-5 py-2.5 font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary min-h-11"
              >
                {t.tryAgain}
              </button>
              <a
                href="/"
                className="rounded-medium border border-default-300 px-5 py-2.5 font-medium text-foreground transition-colors hover:bg-default-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary min-h-11 inline-flex items-center justify-center"
              >
                {t.goHome}
              </a>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
